import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '@/app/hook';
import InfoMessageAlert from '@/features/bot/InfoMessageAlert';
import { selectClient } from '@/features/dashboard.slice';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { selectCurrentDataState } from '../../create.slice';

const SelectLinks = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const client = useAppSelector(selectClient);
  const { clearErrors, setError, setValue, formState } = useFormContext();
  const currentData = useAppSelector(selectCurrentDataState);
  const [sitemaps, setSitemaps] = useState<string[]>([]);
  const [selectedSiteMaps, setSelectedSiteMaps] = useState<string[]>(currentData?.plug_and_play?.links ?? []);
  const targetBotCofig = client?.botConfigs?.find(item => item.botName === currentData?.bot_code);
  const isInitialMount = useRef(true);

  const handleAddOrRemoveLink = useCallback(
    (sitemap: string, isChecked: boolean) => {
      clearErrors('plug_and_play.links');

      setSelectedSiteMaps(prevSelected => {
        const newSelected = new Set(prevSelected);
        const allowedLinks = targetBotCofig?.config?.allowed_links;

        if (isChecked) {
          if (typeof allowedLinks !== 'number' || newSelected.size < allowedLinks) {
            newSelected.add(sitemap);
          } else {
            setError('plug_and_play.links', {
              type: 'custom',
              message: t('Wizard/PlugAndPlayDomainLinks/key4'),
            });
            return prevSelected;
          }
        } else {
          newSelected.delete(sitemap);
        }

        return Array.from(newSelected);
      });
    },
    [clearErrors, setError, t, targetBotCofig?.config?.allowed_links]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setValue('plug_and_play', {
      ...(currentData.plug_and_play || {}),
      links: [...(selectedSiteMaps || [])],
    });
  }, [selectedSiteMaps]);

  useEffect(() => {
    if (currentData?.plug_and_play?.address) {
      setTimeout(() => {
        try {
          const url = new URL(currentData?.plug_and_play?.address);
          if ((currentData?.plug_and_play?.links as string[])?.some(x => x.includes(url.host))) {
            setValue('plug_and_play', {
              ...(currentData.plug_and_play || {}),
              links: [...(currentData.plug_and_play.links || [])],
            });
            setSelectedSiteMaps([...(currentData.plug_and_play.links as string[])]);
          } else {
            setValue('plug_and_play', {
              ...(currentData.plug_and_play || {}),
            });
            setSelectedSiteMaps(currentData.plug_and_play.links || []);
          }
        } catch (error) {
          setValue('plug_and_play', {
            ...(currentData.plug_and_play || {}),
            links: [],
          });
          setSelectedSiteMaps([]);
        }

        setSitemaps(currentData?.plug_and_play?.sitemap || currentData?.plug_and_play?.links || []);
      }, 0);
    }
  }, [currentData?.plug_and_play?.address]);

  return (
    <div className="plug-and-play tw-h-full tw-py-4 md:tw-px-4">
      <div className="tw-w-full tw-h-full tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
        <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-inline-flex">
          <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
            <div className="tw-self-stretch tw-min-h-[60px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
              <div className="tw-self-stretch tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
                2. {t('Wizard/PlugAndPlayDomainLinks/key1')}
              </div>
              <div className="tw-self-stretch tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('Wizard/PlugAndPlayDomainLinks/key2')}
              </div>
            </div>
          </div>
        </div>

        <InfoMessageAlert
          texts={[
            t('Wizard/PlugAndPlayAlert/key1'),
            t('Wizard/PlugAndPlayAlert/key2', {
              size: targetBotCofig?.config?.allowed_links,
            }),
          ]}
        />

        <div className="tw-w-full tw-h-[calc(100%-135px)] tw-p-4 tw-bg-white tw-rounded-xl tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-inline-flex">
          {!!(formState.errors.plug_and_play as any)?.links ||
            (!!(formState.errors.plug_and_play as any)?.links && (
              <div className="tw-pl-2 tw-pr-3 tw-py-1 tw-bg-orange-50 tw-rounded-[99px] tw-justify-start tw-items-center tw-gap-1.5 tw-flex">
                <div className="tw-text-amber-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                  {t('Wizard/PlugAndPlayDomainLinks/er-01')}
                </div>
              </div>
            ))}

          <div className="tw-self-stretch tw-overflow-auto tw-px-2 tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex md:pb-0 pb-4">
            {sitemaps?.map((sitemap, index) => (
              <div key={index} className="tw-self-stretch tw-py-0.5 tw-justify-start tw-items-center tw-gap-2 tw-inline-flex">
                <Form.Check
                  type={'checkbox'}
                  label={sitemap}
                  className="tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
                  onChange={e => {
                    const isChecked = e.target.checked;
                    const allowedLinks = targetBotCofig?.config?.allowed_links;

                    if (isChecked && typeof allowedLinks === 'number' && selectedSiteMaps.length >= allowedLinks) {
                      toast.error(
                        t('Wizard/validationLabels/key27', {
                          size: allowedLinks,
                        })
                      );
                      return;
                    }

                    handleAddOrRemoveLink(sitemap, isChecked);
                  }}
                  checked={selectedSiteMaps.includes(sitemap)}
                  id={`sitemap-${index}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(SelectLinks);
